import { useContext, useEffect, useMemo } from "react";
import { COLUMN_DAY_ARRAY } from "../../utility/keys";
import { AppContext } from "../../context/app.context";
import { ScheduleContext } from "../../context/schedule.context";
import { ModelTsContext } from "../../context/modelTS.context";
import GridSpinner from "../../components/GridSpinner";
import TimeTravelTint from "../../components/TimeTravelTint";
import TimeSlider from "../../components/TimeSlider";
import { shouldHideInstallerRow } from "../../utility/installerHasFullWeekOff";
import {
  buildWeekDatesArray,
  getMondayOfThisWeek,
  getPrevWeekMonday,
  getNextWeekMonday,
  isWeekBeyondLimit,
  canNavigateWeekBackFrom,
} from "../../utility/weekDates";
import ScheduleCell from "./ScheduleCell";
import PerGridToolbar from "./PerGridToolbar";

// - - - - - - - - - -
// Per-grid Optimizer schedule grid (#105 phase 4c).
//
// Fully controlled: parent (OptimizerScreen) owns weekStartDate for this
// grid and passes it in via props. Prev/next/today buttons call
// setWeekStartDate to ask the parent to update. Parent also owns the
// multi-grid array, the close-grid action, and persistence.
//
// Each instance still computes its own scheduleArray + countsArray +
// visibleCandidatesList for its local week via the builders exposed in
// phase 4a, so multi-grid renders correct per-week data.
//
// Helpers that depend on per-week data (isLocked, renderDateHeaderRow,
// renderUnassignedScheduledCells, renderAssignedScheduledCells) are
// received as props from the parent and called with this grid's arrays.

export const OptimizerGrid = ({
  weekStartDate,
  setWeekStartDate,
  onClose,
  canClose,
  singleGrid,
  filteredCandidatesList,
  schedulePreparing,
  renderDateHeaderRow,
  renderUnassignedCell,
  renderUnassignedScheduledCells,
  renderCrewCell,
  renderAssignedScheduledCells,
  renderSelectedSurfaceValues,
  isLocked,
  onCellDrop,
  onCellDragOver,
  onCellDragLeave,
  // Time Machine #114 Phase 6 — Diff Pair view scroll sync. The split-screen
  // attaches scrollContainerRef to grab the scroll element, hideScrollbar
  // suppresses the right grid's own track (sync from left), onScroll fires
  // on the left grid and the parent mirrors the position into the right.
  // All three optional; live mode and single-grid time-travel ignore them.
  scrollContainerRef,
  hideScrollbar = false,
  onScroll,
  hidePerGridToolbar = false,
  // Time Machine #114 Phase 6 — Diff Pair right grid omits the sticky-left
  // "Crews" column so the LEFT grid's crew labels serve both grids. Skips
  // both the header cell and each row's first ScheduleCell.
  omitCrewsColumn = false,
  // Phase 6 — wheel handler attached to the scroll container. The right
  // grid uses this to forward two-finger trackpad scrolls to the left grid
  // (its own overflow:auto would otherwise scroll independently regardless
  // of the hidden scrollbar).
  onWheel,
  // Phase 6 — when true, JobTiles rendered in this grid receive their
  // diff prop (red outline + stacked username bubbles). The LEFT grid
  // shows only T1 state, no diff visuals; the RIGHT grid shows T2 state
  // with diffs flagged. Default false so live mode and single-grid
  // time-travel render normally.
  showDiffs = false,
  // Phase 6 — per-grid time slider. "left" drives T1 (mirrors banner);
  // "right" drives T2 (clamped to >= T1). Null in single-grid / live.
  diffPairSide = null,
  // Phase 6 — fixed cell width (rem). When set, all ScheduleCells in this
  // grid plus the Crews header are rendered with this fixed width. Used by
  // Diff Pair so column widths stay constant regardless of data content
  // and container size; cells that don't fit overflow horizontally.
  cellWidthRem = null,
}) => {
  const {
    locationFilter,
    dataFormatting_payrollFields,
    isTimeTraveling,
    isDiffPairView,
    timeTravelDate,
    timeTravelSliderTime,
    setTimeTravelSliderTime,
    diffPairRightSliderTime,
    setDiffPairRightSliderTime,
  } = useContext(AppContext);
  const {
    showOffInstallers,
    buildScheduleArrayForWeek,
    buildCountsArrayForWeek,
    scheduleArray: globalScheduleArray,
  } = useContext(ScheduleContext);
  const { timeTravelLoading, timeTravelSliderEvents = [] } = useContext(ModelTsContext);

  const weekDatesArray = useMemo(
    () => buildWeekDatesArray(weekStartDate),
    [weekStartDate]
  );

  // Per-grid scheduleArray + countsArray built from this grid's week.
  // globalScheduleArray is included as a "data freshness" signal: when
  // underlying projects/attributes refresh via polling, the global one
  // re-renders, retriggering these memos so we pick up new data.
  const localScheduleArray = useMemo(
    () => buildScheduleArrayForWeek(weekDatesArray),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDatesArray, globalScheduleArray]
  );
  const localCountsArray = useMemo(
    () => buildCountsArrayForWeek(weekDatesArray),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekDatesArray, globalScheduleArray]
  );

  // Per-grid Show 'OFF' filter — uses local week, so multi-grid setups
  // hide different rows per grid based on each grid's visible week.
  const visibleCandidatesList = useMemo(() => {
    if (showOffInstallers) return filteredCandidatesList;
    return filteredCandidatesList.filter((crew) => {
      const payrollNo = dataFormatting_payrollFields(crew.payrollNo);
      return !shouldHideInstallerRow(
        crew,
        payrollNo,
        weekDatesArray,
        localScheduleArray,
        locationFilter
      );
    });
  }, [
    showOffInstallers,
    filteredCandidatesList,
    weekDatesArray,
    localScheduleArray,
    locationFilter,
    dataFormatting_payrollFields,
  ]);

  // Local week-nav handlers — pass new dates up to the parent's setter.
  const onWeekToday = () => setWeekStartDate(getMondayOfThisWeek());
  const onWeekBehind = () => {
    const target = getPrevWeekMonday(weekStartDate);
    if (!isWeekBeyondLimit(target)) setWeekStartDate(target);
  };
  const onWeekAhead = () => setWeekStartDate(getNextWeekMonday(weekStartDate));
  const canNavigateWeekBack = canNavigateWeekBackFrom(weekDatesArray);

  // Phase 6 — attach the wheel handler via addEventListener with
  // passive:false so e.preventDefault() works without the browser console
  // warning ("Unable to preventDefault inside passive event listener").
  // React's onWheel JSX prop registers passively, which silently no-ops
  // preventDefault. Used by the Diff Pair right grid to forward two-finger
  // trackpad scrolls to the LEFT grid.
  useEffect(() => {
    if (typeof onWheel !== "function") return;
    const el = scrollContainerRef && scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel, scrollContainerRef]);

  return (
    <div className="flex-auto flex flex-col overflow-hidden px-4 pt-2 relative">
      {/* TimeTravelTint at THIS level (outside the scroll container) so it
          stays viewport-fixed instead of scrolling with the grid content.
          See scheduleScreen.js for the full explanation. */}
      <TimeTravelTint />
      {!hidePerGridToolbar && (
        <div>
          <PerGridToolbar
            weekDatesArray={weekDatesArray}
            onWeekToday={onWeekToday}
            onWeekBehind={onWeekBehind}
            onWeekAhead={onWeekAhead}
            canNavigateWeekBack={canNavigateWeekBack}
            disabled={schedulePreparing}
            onClose={onClose}
            canClose={canClose}
          />
        </div>
      )}
      {isTimeTraveling && isDiffPairView && diffPairSide && (
        <div
          className="px-2 py-1 flex items-center gap-2"
          style={{
            backgroundColor: "#704214",
            color: "#f5deb3",
          }}
          data-testid={`diffpair-slider-${diffPairSide}`}
        >
          <span className="text-xs font-bold uppercase whitespace-nowrap opacity-90">
            {diffPairSide === "left" ? "T1" : "T2"}
          </span>
          <TimeSlider
            day={timeTravelDate}
            value={
              diffPairSide === "left"
                ? timeTravelSliderTime
                : diffPairRightSliderTime
            }
            events={timeTravelSliderEvents}
            disabled={schedulePreparing || timeTravelLoading}
            onChange={(newTime) => {
              if (!(newTime instanceof Date)) return;
              if (diffPairSide === "left") {
                setTimeTravelSliderTime(newTime);
                return;
              }
              // RIGHT slider drives T2. Clamp to >= T1 — the user can extend
              // the gap between T1 and T2 but never go before T1. T2 also
              // clamped to <= end-of-day-UTC by TimeSlider's own range.
              const t1Ms =
                timeTravelSliderTime instanceof Date
                  ? timeTravelSliderTime.getTime()
                  : null;
              const newMs = newTime.getTime();
              const clampedMs = t1Ms != null ? Math.max(t1Ms, newMs) : newMs;
              setDiffPairRightSliderTime(new Date(clampedMs));
            }}
          />
        </div>
      )}
      <div
        ref={scrollContainerRef}
        onScroll={onScroll}
        className={`overflow-auto h-full w-full mt-2 relative${
          hideScrollbar ? " no-scrollbar" : ""
        }`}
      >
        <GridSpinner show={timeTravelLoading} label="Loading historical data…" />
        {/* min-w-max sizes the inner content to its natural width (sum of
            children's min-widths). Without it, the row blocks match the
            scroll container's width and their right edge eventually
            catches up to the sticky-left "Crews" column during horizontal
            scroll, pulling the Crews column off-screen. With min-w-max
            the rows grow as wide as their cells need, giving the sticky
            element unlimited headroom.
            Single-grid mode skips min-w-max so cells can flex-shrink
            below their natural width to fit the container — keeps the
            grid scroll-free as the user widens the RTS sidebar. */}
        <div className={singleGrid ? "" : "min-w-max"}>
        {schedulePreparing && (
          <div className="flex items-center justify-center h-32 text-skin-muted">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow"></div>
              <span>Loading location data...</span>
            </div>
          </div>
        )}
        {/* Sticky-top header wrapper z-30 (#116 fix). Must sit ABOVE:
            (a) crew rows' `first:sticky first:left-0 first:z-10` first cells —
                otherwise vertical scroll renders crew names from rows below
                ON TOP OF the Unassigned row's first cell (same z-10 + later
                in DOM order = later wins);
            (b) `UserBubbleStack` z-20 — Phase 6 diff bubbles otherwise bleed
                over the date-header row when scrolled.
            Cascade: TimeTravelTint z-40, GridSpinner z-50. */}
        <div className="sticky top-0 z-30 text-white">
          <div className="flex min-h-[2.875rem]">
            {!omitCrewsColumn && (
              <div
                className={
                  cellWidthRem != null
                    ? "flex flex-col justify-center items-center font-semibold bg-header border-b-2 border-yellow p-2 overflow-hidden first:sticky first:left-0 first:z-10"
                    : `flex flex-col justify-center items-center font-semibold bg-header border-b-2 border-yellow ${
                        singleGrid ? "" : "min-w-[7rem]"
                      } p-2 flex-1 overflow-hidden basis-32 first:sticky first:left-0 first:z-10`
                }
                style={
                  cellWidthRem != null
                    ? { width: `${cellWidthRem}rem`, flex: "none" }
                    : undefined
                }
              >
                <span>Crews</span>
              </div>
            )}
            {renderDateHeaderRow(weekDatesArray, localCountsArray, omitCrewsColumn, cellWidthRem)}
          </div>
          <div className="flex min-h-[8rem]">
            {!omitCrewsColumn && (
              <ScheduleCell noMinWidth={singleGrid} widthRem={cellWidthRem}>
                <div className="bg-crew-surface-unassigned text-dark-grey text-sm font-semibold p-2 text-center h-full">
                  {
                    // this renders a single cell, with button: "Unassigned"
                    renderUnassignedCell()
                  }
                </div>
              </ScheduleCell>
            )}
            {
              // this renders all of the unassigned cells, in 1 row only
              COLUMN_DAY_ARRAY.map((value, columnIndex) => (
                <ScheduleCell
                  key={columnIndex}
                  noMinWidth={singleGrid}
                  noFirstSticky={omitCrewsColumn}
                  widthRem={cellWidthRem}
                >
                  <div
                    className="bg-crew-surface-unassigned h-full text-skin-primary flex flex-col"
                    onDrop={(e) =>
                      onCellDrop(
                        e,
                        // Append local date (#105 phase 5 cross-grid drop
                        // bug fix) so downstream parsers don't have to
                        // guess which grid's week to use.
                        "Unassigned_" + value + "_" + weekDatesArray[columnIndex]
                      )
                    }
                    onDragOver={(e) =>
                      !isLocked("", columnIndex, weekDatesArray, localScheduleArray) &&
                      !isLocked("column", columnIndex, weekDatesArray, localScheduleArray) &&
                      onCellDragOver(e)
                    }
                    onDragLeave={onCellDragLeave}
                  >
                    {renderUnassignedScheduledCells(
                      columnIndex,
                      weekDatesArray,
                      localScheduleArray,
                      showDiffs
                    )}
                  </div>
                </ScheduleCell>
              ))
            }
          </div>
        </div>
        {
          // this renders all of the standard crew & job cells, 1 row at a time
          // visibleCandidatesList applies the Optimizer "Hide 'OFF'" toggle (#100);
          // lookups elsewhere still use filteredCandidatesList.
          visibleCandidatesList.map((crew, candidateIndex) => (
            <div className="flex mt-2 min-h-[6.25rem]" key={candidateIndex}>
              {!omitCrewsColumn && (
                <ScheduleCell noMinWidth={singleGrid} widthRem={cellWidthRem}>
                  <div
                    className={`${
                      crew.Scheduling_Surface === "Soft Surface"
                        ? "bg-crew-surface-soft"
                        : "bg-crew-surface-hard"
                    }   p-2 h-full`}
                  >
                    {renderCrewCell(crew)}
                  </div>
                </ScheduleCell>
              )}
              {COLUMN_DAY_ARRAY.map((value, columnIndex) => {
                const isLockedCell =
                  isLocked(crew.payrollNo, columnIndex, weekDatesArray, localScheduleArray) ||
                  isLocked("column", columnIndex, weekDatesArray, localScheduleArray);
                return (
                  <ScheduleCell
                    key={columnIndex}
                    noMinWidth={singleGrid}
                    noFirstSticky={omitCrewsColumn}
                    widthRem={cellWidthRem}
                  >
                    <div
                      className={`${
                        crew.Scheduling_Surface === "Soft Surface"
                          ? "bg-skin-surface-soft"
                          : "bg-skin-surface-hard"
                      }  h-full flex flex-col`}
                      onDrop={(e) =>
                        onCellDrop(
                          e,
                          // Append local date (#105 phase 5 cross-grid
                          // drop bug fix) so the downstream mutation
                          // uses THIS grid's week, not AppContext's.
                          dataFormatting_payrollFields(crew.payrollNo) +
                            "_" +
                            columnIndex +
                            "_" +
                            weekDatesArray[columnIndex]
                        )
                      }
                      onDragOver={(e) => !isLockedCell && onCellDragOver(e)}
                      onDragLeave={onCellDragLeave}
                    >
                      {renderAssignedScheduledCells(
                        columnIndex,
                        crew,
                        isLockedCell,
                        weekDatesArray,
                        localScheduleArray,
                        showDiffs
                      )}
                      <div className="flex-1 flex items-end justify-end p-2">
                        {
                          // bottom right of assigned cells; used to show lock button; now shows surface qty calc
                          renderSelectedSurfaceValues(columnIndex, crew)
                        }
                      </div>
                    </div>
                  </ScheduleCell>
                );
              })}
            </div>
          ))
        }
        </div>
      </div>
    </div>
  );
};

export default OptimizerGrid;
